import React, { useState, useEffect, useRef, useMemo } from "react";
import { injectIntl, IntlShape } from "react-intl";
import { match as Match } from "react-router-dom";
import { toast } from "react-toastify";
import { FieldAttributes } from "formik";
import Block from "../../../../components/Tailwind/Block";
import Header from "../../../../components/Tailwind/Block/Header";
import {
  EnumAttributeType,
  EnumPermissionEntity,
} from "../../../../lib/ground-aws-graphql-core/api/graphql/types";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import DefaultForm, {
  AdditionalFieldAttributes,
} from "../../../../components/Form/index";
import overrideClasses from "../../../../utils/overrideClasses";
import {
  AttributeItem,
  getEntityAttributes,
  getRequiredAttributes,
} from "../../../../utils/attribute";
import { AttributeKey } from "../../../../lib/ground-aws-graphql-core/models/AttributeKey";
import { convertAttributeItems } from "../../../../utils/preprocessing";
import FormSwitch from "../../../../components/Form/FormSwitch";
import CardAttributes from "../../../../components/GroundBlocks/BlockCard/CardAttributes";
import { ALL_LIMIT } from "../../../../utils/config";

interface Props {
  intl: IntlShape;
  match: Match<{ id: string }>;
}

const OperatorForm = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);

  const attributesFormRef = useRef<React.MutableRefObject<any>>();
  const [attributes, setAttributes] = useState<AttributeItem[]>([]);
  const [attributeKeys, setAttributeKeys] = useState<AttributeKey[]>([]);
  const [showPanel, setShowPanel] = useState(false);
  const { intl } = props;

  const searchAllAttributeKeys = GroundGraphqlContextStore.useStoreActions(
    actions => actions.attributeKey.searchAllAttributeKeys
  );

  const getOperator = GroundGraphqlContextStore.useStoreActions(
    actions => actions.operator.getOperator
  );

  const setOperator = GroundGraphqlContextStore.useStoreActions(
    actions => actions.operator.setOperator
  );

  const updateOperator = GroundGraphqlContextStore.useStoreActions(
    actions => actions.operator.updateOperator
  );

  const operator = GroundGraphqlContextStore.useStoreState(
    state => state.operator.operator
  );

  const saveDatas = (formValues?) => {
    setLoading(true);

    const payload = {
      attributes: attributes && convertAttributeItems(attributes),
    };

    // Update
    updateOperator(payload)
      .then(resp => {
        const { status } = resp;
        if (status !== 200) {
          toast(
            intl.formatMessage({
              id: "page.operator.update.error",
            }),
            {
              type: "error",
            }
          );
        } else {
          toast(
            intl.formatMessage({
              id: "page.operator.update.success",
            }),
            { type: "success" }
          );
        }
      })
      .catch(() => {
        toast(intl.formatMessage({ id: "page.operator.update.error" }), {
          type: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);

    const promises = [
      searchAllAttributeKeys({
        limit: ALL_LIMIT,
        filter: {
          type: { eq: EnumAttributeType.OPERATOR },
          enabled: { eq: true },
        },
      }),
      getOperator(null),
    ];

    Promise.all(promises)
      .then(response => {
        setAttributeKeys(response[0].items);
      })
      .finally(() => {
        setLoading(false);
      });

    // Clean user/admin in global state to avoid issues when navigating backwards with the browser
    // Solves user duplication issue
    return () => setOperator(null);
  }, []);

  useEffect(() => {
    if (operator && attributeKeys) {
      const attr = getEntityAttributes(
        operator,
        EnumAttributeType.OPERATOR,
        attributeKeys
      );

      setAttributes([...attr]);
    }
  }, [operator, attributeKeys]);

  const handleChangeAttribute = (newAttributes: AttributeItem[]) => {
    setAttributes([...newAttributes]);
    handleShowSavePanel();
  };

  // FIXME: Remove this when we refactor the second part of the form
  useEffect(() => {
    // Reset the showPanel otherwise the panel won't open again when modifying elements outside of the initial form
    if (showPanel) setShowPanel(false);
  }, [showPanel]);

  const handleShowSavePanel = () => setShowPanel(true);

  const handleSavePanelForm = (values): void => {
    if (attributesFormRef.current) {
      attributesFormRef.current.validateForm().then(data => {
        if (Object.keys(data).length === 0) {
          saveDatas(values);
        } else {
          attributesFormRef.current.showErrors(data);
        }
      });
    } else {
      saveDatas(values);
    }
  };

  const requiredAttributes = getRequiredAttributes(attributeKeys, attributes);

  const formFields: FieldAttributes<AdditionalFieldAttributes>[] = useMemo(
    () => [
      {
        name: "name",
        label: "general.name",
        placeholder: "general.name",
        initialValue: operator?.id,
        required: true,
        disabled: true,
      },
      {
        name: "short_name",
        label: "page.operator.short_name",
        placeholder: "page.operator.short_name",
        initialValue: operator?.short_name,
        required: true,
        disabled: true,
      },
      {
        name: "payment_externalized",
        label: "page.operator.payment_externalized",
        initialValue: operator ? operator?.payment_externalized : false,
        component: props => (
          <FormSwitch
            {...props}
            description="page.operator.payment_externalized.description"
          />
        ),
      },
    ],
    [operator]
  );

  return !operator ? (
    <div className="loading" />
  ) : (
    <>
      {/**
       * When we were loading the form was initialized again and we would lose all data
       * Show loading here in order to not lose the fields data after a loading
       */}
      <div className={overrideClasses({ loading })} />

      {/* Hide when loading */}
      <div className={overrideClasses("px-8 pb-32", { hidden: loading })}>
        <Block>
          <Header
            item={operator}
            title="page.operator.details"
            entity={EnumPermissionEntity.OPERATOR}
            className="border-b border-gray-200"
          />

          <DefaultForm
            fields={formFields}
            onSubmit={handleSavePanelForm}
            showPanel={showPanel}
          />
        </Block>

        <CardAttributes
          {...props}
          item={operator}
          title="general.param.attributes"
          attributes={attributes.concat(requiredAttributes)}
          attributeKeys={attributeKeys}
          onChange={handleChangeAttribute}
          onDelete={handleChangeAttribute}
          type={EnumAttributeType.OPERATOR}
          ref={attributesFormRef}
          zeroMessage="page.operator.attributes.empty"
        />
      </div>
    </>
  );
};

export default injectIntl(OperatorForm);
